import { useMemo } from 'react';
import React, { useEffect, useState } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Text from '@amzn/meridian/text';
import { uniq } from 'lodash';
import { type ApprovalFormData } from 'src/store/newJmrPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { getAustinLink } from 'src/utils/helpers';
import { getGroupedModifications, getRestrictionsForModification } from 'src/utils/jmrUtils';

interface EmailKeys {
  label: string;
  value: JSX.Element | JSX.Element[] | string;
}

interface ApprovalEmailFormProps {
  handleChange: (key: keyof ApprovalFormData, value: any) => void;
}

/**
 * Default component
 */
const ApprovalEmailForm = ({ handleChange }: ApprovalEmailFormProps) => {
  const { alias } = useAppSelector((state) => state.user);
  const { approvalFormData, approvalFormErrors, backgroundFormData, jmrResults, selectedTasks } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const [selectedResultNames, setSelectedResultNames] = useState<string[]>([]);

  useEffect(() => {
    setSelectedResultNames(jmrResults.filter((jmrResult) => jmrResult.isChecked).map((jmrResult) => jmrResult.name));
  }, [jmrResults]);

  // Setup preview data
  const rows: EmailKeys[] = useMemo(() => {
    // Initialize rows
    let retVal: EmailKeys[] = [
      {
        label: 'Associate can work',
        value: backgroundFormData.cannotWorkNormalShiftHrs
          ? backgroundFormData.limitedHoursPerDay || ''
          : backgroundFormData.shiftLength + ' hr',
      },
      {
        label: 'AUSTIN/Gensuite Link',
        value: getAustinLink(backgroundFormData.caseID),
      },
      {
        label: 'Job Placement(s)',
        value: selectedResultNames.length > 0 ? selectedResultNames.reduce((prev, curr) => `${prev}, ${curr}`) : 'None',
      },
      {
        label: 'Requestor',
        value: alias,
      },
    ];
    // Initialize selected task names
    let selectedTaskNamesString = 'None';
    if (selectedTasks.length > 0) {
      selectedTaskNamesString = selectedTasks.map((i) => i.label).join(', ');
    }

    // Show modifications if site is participating in them, otherwise just show the restrictions
    if (backgroundFormData.site?.jobModificationsEnabled) {
      const modifications = getGroupedModifications(jmrResults).map((entry) => ({
        displayName: entry.name + (entry.freeFormText ? `: ${entry.freeFormText}` : ''),
        restrictions: uniq(entry.conflicts.map(getRestrictionsForModification)),
      }));

      retVal = retVal.concat([
        {
          label: 'Restrictions',
          value: selectedTaskNamesString,
        },
        {
          label: 'Modifications',
          value:
            modifications.length > 0 ? (
              <ul style={{ marginTop: 0 }}>
                {modifications.map((mod, i) => (
                  <li style={{ paddingTop: 0 }} key={i}>
                    {mod.displayName}
                    {mod.restrictions.map((r, j) => (
                      <div key={j}>{r}</div>
                    ))}
                  </li>
                ))}
              </ul>
            ) : (
              'N/A'
            ),
        },
      ]);
    } else {
      retVal = retVal.concat([
        {
          label: 'Additional Modifications',
          value: selectedTaskNamesString,
        },
      ]);
    }
    // Return
    return retVal;
  }, [selectedResultNames, backgroundFormData, selectedTasks, jmrResults]);

  return (
    <div>
      <Text type="b300">
        Choose who to send this Job Placement to for approval. Content in this email cannot be edited. Note that this
        information will also send a webhook if setup. An email will be sent to you and anyone you specify below
      </Text>
      <Column spacingInset="400 none 600 none" spacing="300">
        {/* Email Input */}
        <Text type="b300">Email Recipients</Text>
        <Text type="b200" color="secondary">
          Please enter email addresses separated by a semi-colon. Addresses must include @amazon.com.
        </Text>
        <Input
          data-cy="new-jmr__approval__email"
          aria-label="Approval Email"
          placeholder="e.g. jasrulo@amazon.com"
          value={approvalFormData.emailRecipients}
          onChange={(emailRecipients) => handleChange('emailRecipients', emailRecipients)}
          error={approvalFormErrors.emailRecipients?.error}
          errorMessage={approvalFormErrors.emailRecipients?.errorMessage}
        />

        {/* Email Preview */}
        <Box type="outline" spacingInset="400">
          <Text color="secondary" type="b300">
            A Job Safety Analysis has been completed using the physical restrictions outlined by the Associate's medical
            provider. Based on findings, the RTW tool has determined the following role(s) fits the below Associates
            restrictions.
          </Text>
          {rows.map((row, index) => (
            <Column key={index} spacingInset={index === 0 ? '500 none none none' : ''}>
              <Text color="secondary" type="b300">
                {row.label}: {row.value}
              </Text>
            </Column>
          ))}
        </Box>
      </Column>
    </div>
  );
};

export default ApprovalEmailForm;
